<template>
  <MainLayout :title="trans('Main')" class="flex min-h-screen flex-col">
    <div
      class="z-30 bg-theme-background"
      style="padding-top: var(--safe-area-inset-top)"
    >
      <div class="-mx-1 flex items-center p-4 [&>*]:mx-1">
        <ADeepCircleButton
          v-if="restaurant.isWelcomePageEnabled"
          @click="router.push({ name: 'welcome' })"
        >
          <template #icon><ArrowLeftBold /></template>
        </ADeepCircleButton>
        <div class="flex-1"></div>
        <ADeepCircleButton
          v-if="restaurant.forms.length > 0 && isOnline"
          @click="router.push({ name: 'form' })"
        >
          <template #icon><ChatTextBold /></template>
        </ADeepCircleButton>
        <LanguagePickerCircleButton v-if="restaurant.languages.length > 1" />
        <ADeepCircleButton @click="router.push({ name: 'about' })">
          <template #icon><InfoBold /></template>
        </ADeepCircleButton>
      </div>
    </div>
    <div class="relative mx-auto mt-2 w-full max-w-xl px-6 pb-10">
      <div
        v-if="restaurant.logo"
        class="mb-6 h-48 w-full bg-contain bg-center bg-no-repeat"
        :style="{ backgroundImage: `url('${restaurant.logo.url}')` }"
      />
      <h1
        v-if="restaurant.isRestaurantNameDisplayedOnHomePage"
        class="mb-8 text-center font-primary text-xl font-bold"
      >
        {{ restaurant.name }}
      </h1>
      <div v-if="restaurant.menus.length === 0" class="mb-8 text-center">
        {{ trans('There are no active menus at the moment.') }}
      </div>
      <div class="flex flex-col">
        <RouterLink
          v-for="menu in restaurant.menus"
          :key="menu.id"
          :to="{ name: 'menu', params: { menuSlug: menu.slug } }"
          class="menu-button-primary mb-3"
        >
          {{ localize(menu.name) }}
        </RouterLink>
      </div>
      <div class="mt-8 flex flex-col empty:hidden">
        <RouterLink
          v-for="form in restaurant.forms"
          :key="form.id"
          :to="{ name: 'form' }"
          class="menu-button-secondary mb-3"
        >
          <span class="flex items-center justify-center">
            <ChatTextBold
              class="h-5 w-5 flex-shrink-0 dir-ltr:mr-2 dir-rtl:ml-2"
            />
            <span>{{ localize(form.title) }}</span>
          </span>
        </RouterLink>
        <a
          v-if="restaurant.googleMapsUrl"
          :href="restaurant.googleMapsUrl"
          target="_blank"
          rel="nofollow"
          class="menu-button-secondary mb-3"
        >
          <span class="flex items-center justify-center">
            <MapPinBold
              class="h-5 w-5 flex-shrink-0 dir-ltr:mr-2 dir-rtl:ml-2"
            />

            <span>{{ trans('Location') }}</span>
          </span>
        </a>
        <a
          v-if="restaurant.googleReviewsUrl"
          :href="restaurant.googleReviewsUrl"
          target="_blank"
          rel="nofollow"
          class="menu-button-secondary mb-3"
        >
          <span class="flex items-center justify-center">
            <GoogleLogoBold
              class="h-5 w-5 flex-shrink-0 dir-ltr:mr-2 dir-rtl:ml-2"
            />

            <span>{{ trans('Leave Google review') }}</span>
          </span>
        </a>
        <a
          v-if="restaurant.tripadvisorUrl"
          :href="restaurant.tripadvisorUrl"
          target="_blank"
          rel="nofollow"
          class="menu-button-secondary mb-3"
        >
          <span class="flex items-center justify-center">
            <TripAdvisorLogo
              class="h-5 w-5 flex-shrink-0 dir-ltr:mr-2 dir-rtl:ml-2"
            />

            <span>{{ trans('Leave TripAdvisor review') }}</span>
          </span>
        </a>
      </div>
      <div class="mt-4 flex flex-wrap justify-center [&>*]:m-2">
        <a
          v-if="restaurant.instagram"
          :href="restaurant.instagram"
          target="_blank"
          rel="nofollow"
        >
          <InstagramLogoRegular class="h-8 w-8" />
        </a>
        <a
          v-if="restaurant.tiktok"
          :href="restaurant.tiktok"
          target="_blank"
          rel="nofollow"
        >
          <TiktokLogoRegular class="h-8 w-8" />
        </a>
        <a
          v-if="restaurant.facebook"
          :href="restaurant.facebook"
          target="_blank"
          rel="nofollow"
        >
          <FacebookLogoRegular class="h-8 w-8" />
        </a>
        <a
          v-if="restaurant.whatsapp"
          :href="restaurant.whatsapp"
          target="_blank"
          rel="nofollow"
        >
          <WhatsappLogoRegular class="h-8 w-8" />
        </a>
      </div>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import LanguagePickerCircleButton from '@menu/Shared/LanguagePickerCircleButton.vue';
import MainLayout from '@menu/Shared/MainLayout.vue';
import GoogleLogoBold from '@/phosphoricons/GoogleLogoBold.vue';
import WhatsappLogoRegular from '@/phosphoricons/WhatsappLogoRegular.vue';
import FacebookLogoRegular from '@/phosphoricons/FacebookLogoRegular.vue';
import TiktokLogoRegular from '@/phosphoricons/TiktokLogoRegular.vue';
import InstagramLogoRegular from '@/phosphoricons/InstagramLogoRegular.vue';
import TripAdvisorLogo from '@/icons/TripAdvisorLogo.vue';
import ChatTextBold from '@/phosphoricons/ChatTextBold.vue';
import ADeepCircleButton from '@/menu/Shared/ADeepCircleButton.vue';
import InfoBold from '@/phosphoricons/InfoBold.vue';
import ArrowLeftBold from '@/phosphoricons/ArrowLeftBold.vue';
import MapPinBold from '@/phosphoricons/MapPinBold.vue';

import { localize, trans } from '@menu/Utils/i18n';
import { useIsOnline } from '@menu/Composables/useIsOnline';
import { useRouter } from 'vue-router';
import getRestaurant from '@menu/Utils/getRestaurant';

const router = useRouter();

const restaurant = getRestaurant();
const { isOnline } = useIsOnline();
</script>
